import React from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { sectionByKey, isBlankRow } from './sectionDefs';
import AutoGrowTextarea from './AutoGrowTextarea';

// Condensed card editor for the Issue Log — replaces the wide SectionTable on
// the Setup tab so nothing scrolls sideways on a phone. Bools store 'Y'/'N'
// like every other section, so old drafts keep loading.
//
// hideBlank hides untouched rows (the seeded starter row every section gets)
// so the Sheet view — which has no "any issues today?" checkbox to gate them
// — shows just the "+ Add issue" link until there's a real issue. Rows opened
// from that link are tracked in `revealed` so an empty card stays on screen
// while it's being filled in.

const labelStyle = {
    fontSize: 10,
    fontWeight: 600,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
    display: 'block',
};

const inputStyle = {
    width: '100%',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    background: '#fff',
    fontSize: 13,
    color: '#0F172A',
    padding: '8px 10px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
};

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const IssueCards = ({ readOnly, hideBlank }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [revealed, setRevealed] = React.useState([]);
    const def = sectionByKey.delays;

    if (!report) return null;
    const rows = report.sections?.delays || [];
    const shown = (r, i) => !hideBlank || !isBlankRow(r) || revealed.includes(i);

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, delays: next } });
        setDirty(true);
    };
    const updateRow = (i, patch) => {
        const next = rows.slice();
        next[i] = { ...next[i], ...patch };
        setRows(next);
    };
    // Reuse a hidden blank row before appending another one, so "+ Add issue"
    // on a fresh report opens the starter row instead of stacking blanks.
    const addRow = () => {
        const blank = rows.findIndex((r, i) => isBlankRow(r) && !shown(r, i));
        if (blank >= 0) { setRevealed([...revealed, blank]); return; }
        setRevealed([...revealed, rows.length]);
        setRows([...rows, {}]);
    };
    const delRow = (i) => {
        setRevealed(revealed.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)));
        setRows(rows.filter((_, idx) => idx !== i));
    };

    const optionsOf = (name) => def.fields.find((f) => f.name === name)?.options || [];

    const select = (i, r, name, label, options) => (
        <div>
            <label style={labelStyle}>{label}</label>
            <select
                value={r[name] ?? ''}
                disabled={readOnly}
                onChange={(e) => updateRow(i, { [name]: e.target.value })}
                style={{ ...inputStyle, cursor: readOnly ? 'default' : 'pointer', color: r[name] ? '#0F172A' : '#94A3B8' }}
            >
                <option value="">—</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );

    const checkbox = (i, r, name, label) => (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0F172A', fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: readOnly ? 'default' : 'pointer' }}>
            <input
                type="checkbox"
                checked={r[name] === 'Y'}
                disabled={readOnly}
                onChange={(e) => updateRow(i, { [name]: e.target.checked ? 'Y' : 'N' })}
                style={{ width: 16, height: 16, accentColor: '#1D4ED8', cursor: 'inherit' }}
            />
            {label}
        </label>
    );

    const totalHrs = Math.round(rows.reduce((t, r) => t + num(r.durationHrs), 0) * 10) / 10;

    // Cards number by what's on screen, so hiding blanks doesn't skip numbers.
    let visible = 0;
    const issueNo = rows.map((r, i) => {
        if (!shown(r, i)) return null;
        visible += 1;
        return visible;
    });

    return (
        <View>
            {rows.map((r, i) => (issueNo[i] == null ? null : (
                <div key={i} style={{ borderRadius: 12, border: '1px solid #E2E8F0', background: '#fff', padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                            {`Issue ${issueNo[i]}`}
                        </Text>
                        {!readOnly && (
                            <button
                                onClick={() => delRow(i)}
                                title="Remove issue"
                                style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                            >×</button>
                        )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
                        {select(i, r, 'type', 'Type of Issue', optionsOf('type'))}
                        {select(i, r, 'responsibleParty', 'Responsible Party', optionsOf('responsibleParty'))}
                        <div>
                            <label style={labelStyle}>Duration (hrs)</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={r.durationHrs ?? ''}
                                disabled={readOnly}
                                onChange={(e) => updateRow(i, { durationHrs: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Description</label>
                        <AutoGrowTextarea
                            value={String(r.description ?? '')}
                            disabled={readOnly}
                            minHeight={56}
                            placeholder="What happened, where, and the impact…"
                            onChange={(e) => updateRow(i, { description: e.target.value })}
                            style={{ ...inputStyle, display: 'block' }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                        {checkbox(i, r, 'subInvolved', 'Sub involved')}
                        {checkbox(i, r, 'potentialCO', 'Potential CO')}
                        {checkbox(i, r, 'clientNotified', 'Client notified')}
                    </div>
                </div>
            )))}

            {readOnly && visible === 0 && (
                <Text style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>No issues reported.</Text>
            )}

            {!readOnly && (
                <Pressable onPress={addRow} style={{ cursor: 'pointer', paddingTop: 4, paddingBottom: 4, marginBottom: 8, alignSelf: 'flex-start' }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Add issue</Text>
                </Pressable>
            )}

            {totalHrs > 0 && (
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#0F172A', marginBottom: 8 }}>
                    {`Total duration: ${totalHrs} hrs`}
                </Text>
            )}
        </View>
    );
};

export default IssueCards;
